import { useState, useEffect } from "react";
import styles from "./AllStoriesPage.module.css";
import { FiSearch } from "react-icons/fi";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import useDashboardStore from "../../store/dashboardStore";
import dayjs from "dayjs";

const AllStoriesPage = () => {
  const { setIsCreateStoryScreen, setEditStoryId } = useDashboardStore();
  const [stories, setStories] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const fetchStories = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get(ENDPOINTS.GET_ALL_STORIES_ADMIN);
      if (data.success) {
        setStories(data.stories);
      }
    } catch (err) {
      console.error("Failed to fetch stories", err);
      toast.error("Failed to fetch stories");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const handleEdit = (story) => {
    setEditStoryId(story.id);
    setIsCreateStoryScreen();
  };

  const handleDelete = (story) => {
    confirmAlert({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this story?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const { data } = await api.delete(
                ENDPOINTS.DELETE_STORY_BY_ID(story.id)
              );
              if (data.success) {
                fetchStories();
                toast.success(data.message || "Story deleted successfully");
              }
            } catch (err) {
              console.error("Failed to delete story", err);
              toast.error("Failed to delete story");
            }
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const moveStory = async (story, direction) => {
    const currentIndex = stories.indexOf(story);
    if (direction === "up" && currentIndex > 0) {
      await swapSortOrders(story, stories[currentIndex - 1]);
    } else if (direction === "down" && currentIndex < stories.length - 1) {
      await swapSortOrders(story, stories[currentIndex + 1]);
    }
  };

  const swapSortOrders = async (story1, story2) => {
    try {
      const { data } = await api.put(ENDPOINTS.REORDER_STORIES, {
        orders: [
          { id: story1.id, sort_order: story2.sort_order },
          { id: story2.id, sort_order: story1.sort_order },
        ],
      });
      if (data.success) {
        fetchStories();
        toast.success("Stories reordered successfully");
      }
    } catch (err) {
      console.error("Failed to reorder stories", err);
      toast.error("Failed to reorder stories");
    }
  };

  const isExpired = (expires_at) => {
    if (!expires_at) return false;
    return dayjs(expires_at).isBefore(dayjs());
  };

  const filteredStories = stories.filter((story) => {
    const q = search.toLowerCase();
    return (
      story.type?.toLowerCase().includes(q) ||
      story.content?.toLowerCase().includes(q)
    );
  });

  const typeBadgeClass = (type) => {
    if (type === "image") return styles.badgeImage;
    if (type === "video") return styles.badgeVideo;
    return styles.badgeText;
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>Stories</h2>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search by type or content..."
              className={styles.searchInput}
              disabled
            />
            <FiSearch className={styles.searchIcon} />
          </div>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading stories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>Stories</h2>
        <div className={styles.appBarRight}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search by type or content..."
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FiSearch className={styles.searchIcon} />
          </div>
          <button
            className={styles.addButton}
            onClick={() => {
              setEditStoryId(null);
              setIsCreateStoryScreen();
            }}
          >
            + Add Story
          </button>
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order</th>
              <th></th>
              <th>Type</th>
              <th>Content</th>
              <th>Expires At</th>
              <th>Plans</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredStories.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyCell}>
                  No stories found.
                </td>
              </tr>
            ) : (
              filteredStories.map((story, index) => (
                <tr key={story.id}>
                  <td className={styles.orderCell}>{story.sort_order}</td>
                  <td>
                    <div className={styles.reorderButtons}>
                      <button
                        className={styles.arrowButton}
                        onClick={() => moveStory(story, "up")}
                        disabled={index === 0}
                      >
                        ▲
                      </button>
                      <button
                        className={styles.arrowButton}
                        onClick={() => moveStory(story, "down")}
                        disabled={index === filteredStories.length - 1}
                      >
                        ▼
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.typeBadge} ${typeBadgeClass(story.type)}`}>
                      {story.type?.toUpperCase()}
                    </span>
                  </td>
                  <td className={styles.contentCell}>
                    {story.content?.length > 60
                      ? story.content.slice(0, 60) + "…"
                      : story.content}
                  </td>
                  <td>
                    {story.expires_at === null ? (
                      <span className={styles.lifetime}>Lifetime</span>
                    ) : (
                      <span
                        className={
                          isExpired(story.expires_at)
                            ? styles.expired
                            : styles.expiresAt
                        }
                      >
                        {dayjs(story.expires_at).format("DD-MM-YYYY HH:mm")}
                      </span>
                    )}
                  </td>
                  <td className={styles.plansCell}>
                    {!story.plan_ids || story.plan_ids.length === 0 ? (
                      <span className={styles.freeBadge}>Free</span>
                    ) : (
                      story.plan_ids.join(", ")
                    )}
                  </td>
                  <td>
                    <span
                      className={styles.edit}
                      onClick={() => handleEdit(story)}
                    >
                      Edit
                    </span>
                    <span className={styles.divider}> - </span>
                    <span
                      className={styles.delete}
                      onClick={() => handleDelete(story)}
                    >
                      Delete
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllStoriesPage;
